/**
 * Layout for the Transit section (/transit/*): its own nav, and the org's
 * transitions in the sidebar, each folding out into its nodes. Served by the Go
 * transit service, which signs its users in itself: with no session the user
 * goes to /transit/sign-in and comes back here after.
 */
import { useEffect, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Compass, Loader2, Moon, Sun, Users } from 'lucide-react'
import { AccountMenu } from '../components/ui/UserMenu'
import TransitShell from '../components/transit/TransitShell'
import TransitSidebarNav from '../components/transit/TransitSidebarNav'
import TransitTour, { TRANSIT_TOUR } from '../components/transit/TransitTour'
import { TransitSessionContext, type TransitSession } from '../components/transit/session'
import { useBridgeTheme, useColorMode } from '../components/transit/theme'
import { TRANSIT_NAV } from './navConfig'
import { TransitError, transitApi, transitAuth } from '../services/transit'

const TransitLayout = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [session, setSession] = useState<TransitSession | null>(null)
  const [error, setError] = useState<string | null>(null)
  useBridgeTheme()
  const colorMode = useColorMode()

  useEffect(() => {
    transitApi.me().then(setSession).catch((e) => {
      if (e instanceof TransitError && e.status === 401) {
        navigate(`/transit/sign-in?next=${encodeURIComponent(location.pathname + location.search)}`, { replace: true })
      } else {
        setError(e.message)
      }
    })
    // Once per mount: a 401 later in a page is retried by the client itself.
  }, [])

  if (!session) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        {error ? <span className="text-destructive">{error}</span> : <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…</>}
      </div>
    )
  }

  const signOut = async () => {
    await transitAuth.signOut()
    navigate('/transit/sign-in', { replace: true })
  }
  const nav = session.user.role === 'admin' || session.user.role === 'owner' ? [...TRANSIT_NAV, { label: 'People', to: '/transit/people', icon: Users }] : TRANSIT_NAV

  return (
    <TransitSessionContext.Provider value={session}>
      <TransitShell
        nav={nav}
        homeTo="/transit"
        sidebarExtra={<TransitSidebarNav />}
        footer={
          // AccountMenu, not UserMenu: the menu must show the transit session,
          // never the Django account or its settings pages.
          <AccountMenu
            items={[
              { label: 'Take the tour', icon: Compass, onClick: () => window.dispatchEvent(new Event(TRANSIT_TOUR)) },
              colorMode.dark
                ? { label: 'Light mode', icon: Sun, onClick: colorMode.toggle }
                : { label: 'Dark mode', icon: Moon, onClick: colorMode.toggle },
            ]}
            onLogout={signOut}
            userName={session.user.name || session.user.email}
            userOfficeName={session.org.name}
          />
        }
      >
        <Outlet />
        <TransitTour />
      </TransitShell>
    </TransitSessionContext.Provider>
  )
}

export default TransitLayout
